// How much a role — or one member, in place of their role — may comp (spec/07 §6.4; the owner's decision of
// 2026-09-30: "owners can decide how much comp and if no limit, individually for each manager as well").
//
// One choice, then only what that choice needs: follow the role (a member only), the venue's setting, a
// custom number of minutes per comp and per shift, or no limit at all. Minutes move on a stepper — a
// quarter of an hour a press — rather than a free-typed field, and nothing is sent until Save. The server
// decides again: a number is met with the setting (the lower wins, unless the holder may comp past the
// setting), and "no limit" is given only by an owner or by someone whose own comp has none.

import { useTranslation } from '@clavian/i18n/react';
import { Badge, Button, SegmentedControl } from '@clavian/ui-web';
import { useState, type ReactNode } from 'react';

/** The three comp values a role carries, and a member may carry in place of their role's. */
export interface CompValues {
  readonly compUnlimited: boolean;
  readonly compMaxMinutesPerAction: number | null;
  readonly compMaxMinutesPerShift: number | null;
}

export type CompMode = 'role' | 'setting' | 'custom' | 'unlimited';

/** One press of a stepper: a quarter of an hour. */
const STEP_MINUTES = 15;
/** The most a limit names: a day (`ROLE_LIMIT_MAX_MINUTES`). */
const MAX_MINUTES = 24 * 60;
/** Where a custom limit starts: the settings' own defaults (`sessions.compMaxMinutes*`). */
const START = { compMaxMinutesPerAction: 60, compMaxMinutesPerShift: 240 } as const;

/** Which choice these values are. `null` is a member with none of their own: their role's apply. */
export function compModeOf(values: CompValues | null): CompMode {
  if (values === null) return 'role';
  if (values.compUnlimited) return 'unlimited';
  if (values.compMaxMinutesPerAction === null && values.compMaxMinutesPerShift === null) return 'setting';
  return 'custom';
}

/** The values a choice is saved as. */
function valuesFor(mode: CompMode, perAction: number | null, perShift: number | null): CompValues | null {
  switch (mode) {
    case 'role':
      return null;
    case 'unlimited':
      return { compUnlimited: true, compMaxMinutesPerAction: null, compMaxMinutesPerShift: null };
    case 'setting':
      return { compUnlimited: false, compMaxMinutesPerAction: null, compMaxMinutesPerShift: null };
    case 'custom':
      return { compUnlimited: false, compMaxMinutesPerAction: perAction, compMaxMinutesPerShift: perShift };
  }
}

function same(a: CompValues | null, b: CompValues | null): boolean {
  if (a === null || b === null) return a === b;
  return a.compUnlimited === b.compUnlimited && a.compMaxMinutesPerAction === b.compMaxMinutesPerAction && a.compMaxMinutesPerShift === b.compMaxMinutesPerShift;
}

/** A short line for these values, as the read-only view and the member dialog's summary show them. */
export function useCompSummary(): (values: CompValues | null, role?: string) => string {
  const { t } = useTranslation('console');
  return (values, role) => {
    const minutes = (value: number | null) => (value === null ? t('staff.comp.setting') : t('staff.role.limits.minutes', { count: value }));
    switch (compModeOf(values)) {
      case 'role':
        return t('staff.comp.followsRole', { role: role ?? '' });
      case 'unlimited':
        return t('staff.comp.unlimited');
      case 'setting':
        return t('staff.comp.settingApplies');
      case 'custom':
        return t('staff.comp.customSummary', {
          action: minutes(values?.compMaxMinutesPerAction ?? null),
          shift: minutes(values?.compMaxMinutesPerShift ?? null),
        });
    }
  };
}

export interface CompLimitEditorProps {
  /** As saved: a role's values, or a member's own (`null`: they follow their role). */
  readonly value: CompValues | null;
  /** A member's editor offers "Follow role"; a role's does not. */
  readonly forMember: boolean;
  /** The member's role, for "Follows the cashier role". */
  readonly roleName?: string;
  readonly canEdit: boolean;
  /** Only an owner gives "no limit" (the server holds the full rule); anyone else sees it, disabled. */
  readonly canGiveUnlimited: boolean;
  readonly busy: boolean;
  /** Send it; resolves `true` when the server took it. */
  readonly onSave: (next: CompValues | null) => Promise<boolean>;
}

export function CompLimitEditor(props: CompLimitEditorProps): ReactNode {
  // A new saved value (another save, a refetch) starts the draft again from it.
  return <CompLimitDraft key={JSON.stringify(props.value)} {...props} />;
}

function CompLimitDraft({ value, forMember, roleName, canEdit, canGiveUnlimited, busy, onSave }: CompLimitEditorProps): ReactNode {
  const { t } = useTranslation('console');
  const summary = useCompSummary();
  const [mode, setMode] = useState<CompMode>(() => compModeOf(value));
  // A custom limit starts from what is saved, a `null` in it included (that one limit is the setting's);
  // any other choice starts the steppers at the settings' defaults, for when "Custom" is picked.
  const saved = compModeOf(value) === 'custom' ? value : null;
  const [perAction, setPerAction] = useState<number | null>(saved === null ? START.compMaxMinutesPerAction : saved.compMaxMinutesPerAction);
  const [perShift, setPerShift] = useState<number | null>(saved === null ? START.compMaxMinutesPerShift : saved.compMaxMinutesPerShift);

  if (!canEdit) {
    return <p className="console-num">{summary(value, roleName)}</p>;
  }

  const next = valuesFor(mode, perAction, perShift);
  const dirty = !same(next, value);

  const options = [
    ...(forMember ? [{ value: 'role' as const, label: t('staff.comp.modeRole') }] : []),
    { value: 'setting' as const, label: t('staff.comp.modeSetting') },
    { value: 'custom' as const, label: t('staff.comp.modeCustom') },
    { value: 'unlimited' as const, label: t('staff.comp.modeUnlimited'), disabled: !canGiveUnlimited && compModeOf(value) !== 'unlimited' },
  ];

  const hint: Record<CompMode, string> = {
    role: t('staff.comp.hintRole', { role: roleName ?? '' }),
    setting: t('staff.comp.hintSetting'),
    custom: t('staff.comp.hintCustom'),
    unlimited: t('staff.comp.hintUnlimited'),
  };

  return (
    <div className="console-comp-limit">
      <SegmentedControl label={t('staff.comp.title')} options={options} value={mode} onValueChange={setMode} size="sm" block />
      <p className="console-subtle console-comp-limit__hint">{hint[mode]}</p>
      {!canGiveUnlimited && mode !== 'unlimited' ? <p className="console-subtle console-comp-limit__hint">{t('staff.comp.unlimitedOwnerOnly')}</p> : null}

      {mode === 'custom' ? (
        <div className="console-comp-limit__steppers">
          <MinutesStepper label={t('staff.comp.perAction')} value={perAction} fallback={START.compMaxMinutesPerAction} onChange={setPerAction} />
          <MinutesStepper label={t('staff.comp.perShift')} value={perShift} fallback={START.compMaxMinutesPerShift} onChange={setPerShift} />
        </div>
      ) : null}

      {mode === 'unlimited' ? (
        <p>
          <Badge tone="warn" dot>
            {t('staff.comp.unlimitedBadge')}
          </Badge>
        </p>
      ) : null}

      <div className="console-form__actions">
        <Button
          variant="primary"
          size="sm"
          busy={busy}
          disabled={!dirty}
          onClick={() => {
            void onSave(next);
          }}
        >
          {t('staff.comp.save')}
        </Button>
      </div>
    </div>
  );
}

/**
 * Minutes on a stepper: − and + move a quarter of an hour, between none and a day. `null` is the venue's
 * setting for this one limit; − from the lowest step goes back to it, + from it starts at `fallback`.
 */
function MinutesStepper({
  label,
  value,
  fallback,
  onChange,
}: {
  readonly label: string;
  readonly value: number | null;
  readonly fallback: number;
  readonly onChange: (next: number | null) => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const down = () => {
    if (value === null) return;
    onChange(value <= 0 ? null : Math.max(0, value - STEP_MINUTES));
  };
  const up = () => onChange(value === null ? fallback : Math.min(MAX_MINUTES, value + STEP_MINUTES));
  return (
    <div className="console-comp-limit__stepper" role="group" aria-label={label}>
      <span className="console-comp-limit__stepper-label">{label}</span>
      <span className="console-comp-limit__stepper-control">
        <Button size="sm" variant="ghost" label={t('staff.comp.less', { what: label })} disabled={value === null} onClick={down}>
          −
        </Button>
        <span className="console-num console-comp-limit__value" aria-live="polite">
          {value === null ? t('staff.comp.setting') : t('staff.role.limits.minutes', { count: value })}
        </span>
        <Button size="sm" variant="ghost" label={t('staff.comp.more', { what: label })} disabled={value !== null && value >= MAX_MINUTES} onClick={up}>
          +
        </Button>
      </span>
    </div>
  );
}
